import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import {
  formatMbcbFilingMonthLabel,
  formatMbcbReportPeriod,
  getPreviousMonthMinistryPeriod,
} from '../utils/mbcbReports';
import {
  approveMbcbReport,
  getAllHubMbcbReports,
  getFundingPlantersForHub,
  MBCB_PLANTING_STEP_SCOPE,
  requestMbcbDraftsForPeriod,
  requestMbcbRevision,
  sendMbcbReportReminder,
  type HubMbcbReportListItem,
  type Planter,
} from '../api/plantersService';

function axiosErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const msg = err.response?.data?.message;
    return typeof msg === 'string' ? msg : err.message;
  }
  return err instanceof Error ? err.message : 'Something went wrong';
}

/** Status of a single report-month cell for a planter. */
type CellStatus =
  | 'none'
  | 'draft'
  | 'needs_revision'
  | 'submitted'
  | 'under_review'
  | 'approved'
  | 'rejected';

interface Period {
  year: number;
  month: number;
}

interface Cell extends Period {
  report: HubMbcbReportListItem | null;
  status: CellStatus;
}

interface PlanterTimeline {
  planter: Planter;
  cells: Cell[];
}

interface SelectedCell {
  planter: Planter;
  cell: Cell;
}

const MAX_MONTHS = 36;

const monthIndex = ({ year, month }: Period): number => year * 12 + (month - 1);
const fromMonthIndex = (idx: number): Period => ({
  year: Math.floor(idx / 12),
  month: (idx % 12) + 1,
});

/** Short chip label, e.g. "Apr '26". */
const chipLabel = ({ year, month }: Period): string => {
  const m = new Date(2000, month - 1).toLocaleString('default', { month: 'short' });
  return `${m} '${String(year).slice(-2)}`;
};

const periodKey = ({ year, month }: Period): string => `${year}-${month}`;

const parseMonthFromDate = (value?: string | null): Period | null => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

const deriveStatus = (report: HubMbcbReportListItem | null): CellStatus => {
  if (!report) return 'none';
  return (report.status as CellStatus) ?? 'draft';
};

const CHIP_CLASS: Record<CellStatus, string> = {
  none: 'bg-white border-dashed border-gray-300 text-gray-400 hover:border-gray-500 hover:text-gray-600',
  draft: 'bg-gray-300 border-gray-400 text-gray-800 hover:bg-gray-400',
  needs_revision: 'bg-orange-300 border-orange-400 text-orange-900 hover:bg-orange-400',
  submitted: 'bg-yellow-300 border-yellow-400 text-yellow-900 hover:bg-yellow-400',
  under_review: 'bg-yellow-300 border-yellow-400 text-yellow-900 hover:bg-yellow-400',
  approved: 'bg-emerald-500 border-emerald-600 text-white hover:bg-emerald-600',
  rejected: 'bg-red-400 border-red-500 text-white hover:bg-red-500',
};

const STATUS_TEXT: Record<CellStatus, string> = {
  none: 'No report',
  draft: 'Draft (not submitted)',
  needs_revision: 'Returned for revision',
  submitted: 'Submitted — needs review',
  under_review: 'Under review',
  approved: 'Approved',
  rejected: 'Rejected',
};

const LEGEND: { status: CellStatus; label: string }[] = [
  { status: 'none', label: 'No report' },
  { status: 'draft', label: 'Draft' },
  { status: 'needs_revision', label: 'Needs revision' },
  { status: 'submitted', label: 'Needs review' },
  { status: 'approved', label: 'Approved' },
];

const Reports: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const roleNames = useMemo(
    () => (user?.roles ?? []).map((role) => (typeof role === 'string' ? role : role.name)),
    [user],
  );
  const isStaffish = roleNames.some((r) => ['admin', 'staff', 'mbcb'].includes(r));
  const assignedHubLeader = !isStaffish && roleNames.includes('hub_leader') ? user?.id ?? null : null;

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<SelectedCell | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const latestDuePeriod = useMemo(() => getPreviousMonthMinistryPeriod(), []);

  const fundingQuery = useQuery({
    queryKey: ['hubFundingPlanters', assignedHubLeader],
    queryFn: () => getFundingPlantersForHub(assignedHubLeader),
    staleTime: 60_000,
  });

  const reportsQuery = useQuery({
    queryKey: ['hubAllMbcbReports', assignedHubLeader],
    queryFn: () => getAllHubMbcbReports(),
    staleTime: 30_000,
  });

  // planterId -> (periodKey -> report)
  const reportsByPlanter = useMemo(() => {
    const map = new Map<number, Map<string, HubMbcbReportListItem>>();
    for (const report of reportsQuery.data ?? []) {
      let inner = map.get(report.planterId);
      if (!inner) {
        inner = new Map();
        map.set(report.planterId, inner);
      }
      inner.set(periodKey({ year: report.reportYear, month: report.reportMonth }), report);
    }
    return map;
  }, [reportsQuery.data]);

  const timelines: PlanterTimeline[] = useMemo(() => {
    const planters = [...(fundingQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    const dueIdx = monthIndex(latestDuePeriod);

    return planters.map((planter) => {
      const reportMap = reportsByPlanter.get(planter.id) ?? new Map<string, HubMbcbReportListItem>();
      const reportPeriods = Array.from(reportMap.values()).map((r) => ({
        year: r.reportYear,
        month: r.reportMonth,
      }));

      // Anchor the timeline at funding start (training completion) — but never hide an
      // existing report, so also fold in the earliest report we have on file.
      const fundingStart = parseMonthFromDate(planter.trainingCompletedAt);
      const startCandidates: number[] = [];
      if (fundingStart) startCandidates.push(monthIndex(fundingStart));
      reportPeriods.forEach((p) => startCandidates.push(monthIndex(p)));
      if (startCandidates.length === 0) startCandidates.push(dueIdx);
      let startIdx = Math.min(...startCandidates);

      // End at the latest elapsed ministry month, extended if a later report exists.
      const endCandidates: number[] = [dueIdx];
      reportPeriods.forEach((p) => endCandidates.push(monthIndex(p)));
      let endIdx = Math.max(...endCandidates);
      if (endIdx < startIdx) endIdx = startIdx;

      // Cap to the most recent MAX_MONTHS.
      if (endIdx - startIdx + 1 > MAX_MONTHS) startIdx = endIdx - (MAX_MONTHS - 1);

      const cells: Cell[] = [];
      for (let idx = endIdx; idx >= startIdx; idx -= 1) {
        const period = fromMonthIndex(idx);
        const report = reportMap.get(periodKey(period)) ?? null;
        cells.push({ ...period, report, status: deriveStatus(report) });
      }
      return { planter, cells };
    });
  }, [fundingQuery.data, reportsByPlanter, latestDuePeriod]);

  const filteredTimelines = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return timelines;
    return timelines.filter(({ planter }) =>
      [planter.name, planter.churchName, planter.city, planter.state]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  }, [timelines, search]);

  const summary = useMemo(() => {
    const dueKey = periodKey(latestDuePeriod);
    let missingLatest = 0;
    let awaitingReview = 0;
    let outstanding = 0;
    for (const { planter, cells } of timelines) {
      const reportMap = reportsByPlanter.get(planter.id);
      if (!reportMap?.has(dueKey)) missingLatest += 1;
      for (const cell of cells) {
        if (cell.status === 'submitted' || cell.status === 'under_review') awaitingReview += 1;
        else if (cell.status === 'draft' || cell.status === 'needs_revision') outstanding += 1;
      }
    }
    return { total: timelines.length, missingLatest, awaitingReview, outstanding };
  }, [timelines, reportsByPlanter, latestDuePeriod]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['hubAllMbcbReports', assignedHubLeader] });
  };

  const closeModal = () => {
    setSelected(null);
    setReviewNotes('');
  };

  const openCell = (planter: Planter, cell: Cell) => {
    setReviewNotes('');
    setError(null);
    setFeedback(null);
    setSelected({ planter, cell });
  };

  const createDraftMutation = useMutation({
    mutationFn: (vars: { planterId: number; year: number; month: number }) =>
      requestMbcbDraftsForPeriod({
        reportMonth: vars.month,
        reportYear: vars.year,
        planterId: vars.planterId,
        plantingStepScope: MBCB_PLANTING_STEP_SCOPE.FUNDING,
      }),
    onSuccess: (data, vars) => {
      const label = formatMbcbFilingMonthLabel(vars.month, vars.year);
      setFeedback(data.createdCount > 0 ? `Draft created for ${label}.` : 'A report already exists for that month.');
      invalidate();
      closeModal();
    },
    onError: (e) => setError(axiosErrorMessage(e)),
  });

  const createLatestDraftsMutation = useMutation({
    mutationFn: () =>
      requestMbcbDraftsForPeriod({
        reportMonth: latestDuePeriod.month,
        reportYear: latestDuePeriod.year,
        plantingStepScope: MBCB_PLANTING_STEP_SCOPE.FUNDING,
      }),
    onSuccess: (data) => {
      const label = formatMbcbFilingMonthLabel(latestDuePeriod.month, latestDuePeriod.year);
      setFeedback(`${label} drafts: ${data.createdCount} created · ${data.skippedCount} already existed.`);
      invalidate();
    },
    onError: (e) => setError(axiosErrorMessage(e)),
  });

  const remindMutation = useMutation({
    mutationFn: (reportId: number) => sendMbcbReportReminder(reportId),
    onSuccess: (result) => {
      const channels = [
        result.push?.successfulSends ? 'push' : null,
        result.emailSent ? 'email' : null,
      ].filter(Boolean);
      setFeedback(channels.length ? `Reminder sent (${channels.join(' + ')}).` : 'Reminder processed.');
      closeModal();
    },
    onError: (e) => setError(axiosErrorMessage(e)),
  });

  const approveMutation = useMutation({
    mutationFn: (vars: { reportId: number; notes?: string }) => approveMbcbReport(vars.reportId, vars.notes),
    onSuccess: () => {
      setFeedback('Report approved.');
      invalidate();
      closeModal();
    },
    onError: (e) => setError(axiosErrorMessage(e)),
  });

  const revisionMutation = useMutation({
    mutationFn: (vars: { reportId: number; notes: string }) => requestMbcbRevision(vars.reportId, vars.notes),
    onSuccess: () => {
      setFeedback('Revision requested.');
      invalidate();
      closeModal();
    },
    onError: (e) => setError(axiosErrorMessage(e)),
  });

  const onCreateLatestDrafts = () => {
    const label = formatMbcbFilingMonthLabel(latestDuePeriod.month, latestDuePeriod.year);
    const ministry = formatMbcbReportPeriod(latestDuePeriod.month, latestDuePeriod.year);
    if (
      window.confirm(
        `Create ${label} draft reports (covering ${ministry} ministry) for every funding-stage planter who doesn't have one yet? Existing reports are left untouched.`,
      )
    ) {
      createLatestDraftsMutation.mutate();
    }
  };

  const isLoading = fundingQuery.isLoading || reportsQuery.isLoading;
  const mutating =
    createDraftMutation.isPending ||
    remindMutation.isPending ||
    approveMutation.isPending ||
    revisionMutation.isPending;

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Monthly Reports</h1>
            <p className="mt-2 max-w-3xl text-sm text-gray-600">
              Each row is a <span className="font-medium">funding-stage</span> planter. Every box is one
              month of ministry, starting when they finished training (entered funding). Click a box to
              act on that month.
            </p>
          </div>
          <button
            type="button"
            onClick={onCreateLatestDrafts}
            disabled={createLatestDraftsMutation.isPending || isLoading}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
          >
            {createLatestDraftsMutation.isPending
              ? 'Creating…'
              : `Create ${formatMbcbFilingMonthLabel(latestDuePeriod.month, latestDuePeriod.year)} drafts`}
          </button>
        </div>

        {/* Summary */}
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: 'Funding planters', value: summary.total, tone: 'text-gray-900' },
            {
              label: `Missing ${formatMbcbFilingMonthLabel(latestDuePeriod.month, latestDuePeriod.year)}`,
              value: summary.missingLatest,
              tone: 'text-gray-700',
            },
            { label: 'Awaiting your review', value: summary.awaitingReview, tone: 'text-yellow-700' },
            { label: 'Outstanding drafts', value: summary.outstanding, tone: 'text-orange-700' },
          ].map((card) => (
            <div key={card.label} className="rounded-lg bg-white px-4 py-3 shadow">
              <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{card.label}</dt>
              <dd className={`mt-1 text-2xl font-semibold ${card.tone}`}>{card.value}</dd>
            </div>
          ))}
        </div>

        {/* Legend + search */}
        <div className="mb-4 flex flex-col gap-3 rounded-lg bg-white p-4 shadow sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {LEGEND.map(({ status, label }) => (
              <span key={status} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
                <span className={`inline-block h-4 w-4 rounded border ${CHIP_CLASS[status].split(' hover:')[0]}`} />
                {label}
              </span>
            ))}
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search planters…"
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:max-w-xs sm:text-sm"
          />
        </div>

        {(feedback || error) && (
          <div
            className={`mb-4 rounded-md border px-3 py-2 text-sm ${
              error ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-800'
            }`}
          >
            {error || feedback}
          </div>
        )}

        {/* Timelines */}
        <div className="space-y-3">
          {isLoading ? (
            <div className="rounded-lg bg-white p-6 text-center text-sm text-gray-500 shadow">Loading…</div>
          ) : filteredTimelines.length === 0 ? (
            <div className="rounded-lg bg-white p-6 text-center text-sm text-gray-500 shadow">
              {timelines.length === 0
                ? 'No funding-stage planters found.'
                : 'No planters match your search.'}
            </div>
          ) : (
            filteredTimelines.map(({ planter, cells }) => (
              <div
                key={planter.id}
                className="rounded-lg bg-white p-4 shadow sm:flex sm:items-start sm:gap-4"
              >
                <div className="mb-3 min-w-0 sm:mb-0 sm:w-56 sm:flex-shrink-0">
                  <Link
                    to={`/planters/${planter.id}`}
                    className="text-sm font-semibold text-gray-900 hover:text-primary-700"
                  >
                    {planter.name}
                  </Link>
                  {planter.churchName && (
                    <div className="truncate text-xs text-gray-500">{planter.churchName}</div>
                  )}
                  {!planter.hasPlanterAppUserLinked && (
                    <span className="mt-1 inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                      No app account
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {cells.map((cell) => (
                    <button
                      key={periodKey(cell)}
                      type="button"
                      onClick={() => openCell(planter, cell)}
                      title={`${chipLabel(cell)} — ${STATUS_TEXT[cell.status]}`}
                      className={`rounded border px-2 py-1 text-[11px] font-medium leading-none transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400 ${CHIP_CLASS[cell.status]}`}
                    >
                      {chipLabel(cell)}
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {(fundingQuery.isError || reportsQuery.isError) && (
          <p className="mt-4 text-sm text-red-600">
            Could not load reporting data. Please refresh and try again.
          </p>
        )}
      </div>

      {selected && (
        <CellModal
          selected={selected}
          reviewNotes={reviewNotes}
          setReviewNotes={setReviewNotes}
          mutating={mutating}
          onClose={closeModal}
          onCreateDraft={() =>
            createDraftMutation.mutate({
              planterId: selected.planter.id,
              year: selected.cell.year,
              month: selected.cell.month,
            })
          }
          onRemind={() => selected.cell.report && remindMutation.mutate(selected.cell.report.id)}
          onApprove={() =>
            selected.cell.report &&
            approveMutation.mutate({ reportId: selected.cell.report.id, notes: reviewNotes.trim() || undefined })
          }
          onRequestRevision={() =>
            selected.cell.report &&
            revisionMutation.mutate({ reportId: selected.cell.report.id, notes: reviewNotes.trim() })
          }
        />
      )}
    </Layout>
  );
};

interface CellModalProps {
  selected: SelectedCell;
  reviewNotes: string;
  setReviewNotes: (v: string) => void;
  mutating: boolean;
  onClose: () => void;
  onCreateDraft: () => void;
  onRemind: () => void;
  onApprove: () => void;
  onRequestRevision: () => void;
}

const CellModal: React.FC<CellModalProps> = ({
  selected,
  reviewNotes,
  setReviewNotes,
  mutating,
  onClose,
  onCreateDraft,
  onRemind,
  onApprove,
  onRequestRevision,
}) => {
  const { planter, cell } = selected;
  const { status, report } = cell;
  const filingLabel = formatMbcbFilingMonthLabel(cell.month, cell.year);
  const ministryLabel = formatMbcbReportPeriod(cell.month, cell.year);
  const detailHref = report ? `/planters/${planter.id}/reports/${report.id}` : null;
  const canRemind = status === 'draft' || status === 'needs_revision';
  const isReviewable = status === 'submitted' || status === 'under_review';

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
        <button type="button" aria-label="Close" className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative z-10 w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
          <h3 className="text-lg font-semibold text-gray-900">{filingLabel} report</h3>
          <p className="mt-1 text-sm text-gray-500">
            {planter.name} · covers {ministryLabel} ministry · <span className="font-medium">{STATUS_TEXT[status]}</span>
          </p>

          {/* No report → create draft */}
          {status === 'none' && (
            <div className="mt-5">
              <p className="text-sm text-gray-600">
                No report exists for this month yet. Create a draft so {planter.name} can fill it in
                from the app.
              </p>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={onCreateDraft}
                  disabled={mutating}
                  className="rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {mutating ? 'Creating…' : 'Create draft'}
                </button>
              </div>
            </div>
          )}

          {/* Draft / needs revision → remind */}
          {canRemind && (
            <div className="mt-5">
              <p className="text-sm text-gray-600">
                {status === 'draft'
                  ? 'This report is still a draft and has not been submitted.'
                  : 'This report was returned to the planter for revision.'}
              </p>
              {!planter.hasPlanterAppUserLinked && (
                <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  This planter has no linked app account, so a reminder can't be delivered yet.
                </p>
              )}
              <div className="mt-6 flex flex-wrap justify-end gap-2">
                {detailHref && (
                  <Link
                    to={detailHref}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Open report
                  </Link>
                )}
                <button
                  type="button"
                  onClick={onRemind}
                  disabled={mutating || !planter.hasPlanterAppUserLinked}
                  className="rounded-md border border-transparent bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {mutating ? 'Sending…' : 'Send reminder'}
                </button>
              </div>
            </div>
          )}

          {/* Submitted / under review → approve or request revision */}
          {isReviewable && (
            <div className="mt-5">
              <p className="text-sm text-gray-600">
                The planter submitted this report. Approve it, or send it back with revision notes.
              </p>
              <label className="mt-4 block text-sm font-medium text-gray-700">
                Notes <span className="font-normal text-gray-400">(required to request a revision)</span>
              </label>
              <textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                rows={3}
                className="mt-1 block w-full rounded-md border border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="Optional note on approval, or what to change for a revision…"
              />
              <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
                {detailHref ? (
                  <Link to={detailHref} className="text-sm font-medium text-primary-600 hover:text-primary-800">
                    Open full report
                  </Link>
                ) : (
                  <span />
                )}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={onRequestRevision}
                    disabled={mutating || !reviewNotes.trim()}
                    className="rounded-md border border-transparent bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                  >
                    Request revision
                  </button>
                  <button
                    type="button"
                    onClick={onApprove}
                    disabled={mutating}
                    className="rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    {mutating ? 'Working…' : 'Approve'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Approved / rejected → view */}
          {(status === 'approved' || status === 'rejected') && (
            <div className="mt-5">
              <p className="text-sm text-gray-600">
                {status === 'approved' ? 'This report has been approved.' : 'This report was rejected.'}
                {report?.reviewedAt ? ` (${new Date(report.reviewedAt).toLocaleDateString()})` : ''}
              </p>
              {report?.reviewNotes && (
                <p className="mt-2 whitespace-pre-wrap rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">
                  {report.reviewNotes}
                </p>
              )}
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                {detailHref && (
                  <Link
                    to={detailHref}
                    className="rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                  >
                    Open full report
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
